import { useMemo, useState } from 'react';
import {
  AlertCircleIcon,
  ExternalLinkIcon,
  Loader2 as Loader,
} from 'lucide-react';
import { useOfferById } from '@/refactored/hooks/useOffers';
import LayoutGap from '@/components/Common/LayoutGap';
import Body, { Size as BodySize } from '@/components/Typography/Body';
import Heading, { Size as HeadingSize } from '@/components/Typography/Heading';
import { AlertDanger } from '@/components/ui/alert';
import { useOfferLinks, OfferLinkResponse } from '@/hooks/api/offerLinks';
import { DisplayValue } from '@/components/Common/DisplayData';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import ButtonGroup from '@/components/Common/ButtonGroup';
import { ExperimentResponse } from '@/hooks/api/experiments';
import { orderBy } from 'lodash';
import CreateOfferLink from './Create';
import UpdateOfferLink from './Update';
import OfferLinkView from './View';

type OfferLinkListItemProps = {
  offerLink: OfferLinkResponse;
  experiment: ExperimentResponse;
  onUpdateOfferLinkSuccess: (offerLink: OfferLinkResponse) => void;
};

const OfferLinkListItem = ({
  offerLink,
  experiment,
  onUpdateOfferLinkSuccess,
}: OfferLinkListItemProps) => {
  const [{ data: offer, loading: offerLoading, error: offerError }] =
    useOfferById(offerLink.attributes.offerId, {
      manual: false,
    });

  const label = useMemo(() => {
    if (offerLoading) {
      const skeletonSize = Math.floor(Math.random() * 200) + 100;
      return (
        <Skeleton className="h-4" style={{ width: `${skeletonSize}px` }} />
      );
    }

    if (offerError) {
      return (
        <LayoutGap size="small" className="items-center text-red-500">
          <AlertCircleIcon className="h-4 w-4" />
          Error loading offer name
        </LayoutGap>
      );
    }

    if (offer) {
      return (
        <Link
          to={`/offers/edit/${offer._id}`}
          target="_blank"
          className="flex items-center gap-1 text-primary-600 hover:underline"
        >
          {offer.name}
          <ExternalLinkIcon className="h-3 w-3" />
        </Link>
      );
    }

    return 'No offer name';
  }, [offer, offerLoading, offerError]);

  return (
    <div className="border-b-border-primary-500 grid grid-cols-[1fr,auto] items-end gap-4 border-t pt-4">
      <LayoutGap direction="column">
        <DisplayValue
          label={label}
          type="id"
          value={offerLink.attributes.offerId}
        />
        {offer && (
          <LayoutGap direction="row">
            <DisplayValue
              label="Start Date"
              type="datetime"
              value={offer.startDate.toString()}
            />
            <DisplayValue
              label="Expiration Date"
              type="datetime"
              value={offer.expirationDate.toString()}
            />
          </LayoutGap>
        )}
      </LayoutGap>
      <ButtonGroup className="place-self-end">
        {offerLink.attributes.status === 'DRAFT' && (
          <UpdateOfferLink
            offerLink={offerLink}
            experiment={experiment}
            onSuccess={onUpdateOfferLinkSuccess}
          />
        )}
        {offerLink.attributes.status === 'COMPLETED' && (
          <OfferLinkView offerLink={offerLink} />
        )}
      </ButtonGroup>
    </div>
  );
};

type OfferLinksListProps = {
  experiment: ExperimentResponse;
  onUpdateOfferLinkSuccess: (offerLink: OfferLinkResponse) => void;
};

const OfferLinksList = ({
  experiment,
  onUpdateOfferLinkSuccess,
}: OfferLinksListProps) => {
  const [{ data: offerLinks, loading, error }] = useOfferLinks(experiment.id);

  if (loading) {
    return <Loader className="animate-spin" />;
  }

  if (error) {
    return (
      <AlertDanger
        title="Error loading offer links"
        description={error.message}
      />
    );
  }

  if (offerLinks?.data.length === 0) {
    return <Body size={BodySize.XS}>No offers in this experiment.</Body>;
  }

  return (
    <div className="grid w-fit grid-cols-1 gap-4">
      {orderBy(offerLinks?.data, 'createdAt', 'desc').map((offerLink) => (
        <OfferLinkListItem
          key={offerLink.id}
          offerLink={offerLink}
          experiment={experiment}
          onUpdateOfferLinkSuccess={onUpdateOfferLinkSuccess}
        />
      ))}
    </div>
  );
};

type OfferLinksProps = {
  experiment: ExperimentResponse;
};

const OfferLinks = ({ experiment }: OfferLinksProps) => {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <LayoutGap direction="column" size="large" className="py-4">
      <LayoutGap size="large">
        <Heading size={HeadingSize.S} className="m-0">
          Offer Links
        </Heading>
        <CreateOfferLink
          experiment={experiment}
          onSuccess={() => setRefreshKey(Date.now())}
        />
      </LayoutGap>
      <OfferLinksList
        key={refreshKey}
        experiment={experiment}
        onUpdateOfferLinkSuccess={() => setRefreshKey(Date.now())}
      />
    </LayoutGap>
  );
};

export default OfferLinks;
